import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TopicCard from "@/components/feature/TopicCard";
import { hotTopics, topicCategories } from "@/mocks/topics";

export default function TopicDetail() {
  const { id } = useParams<{ id: string }>();
  const topic = hotTopics.find((item) => item.id === id);

  if (!topic) {
    return (
      <section className="mx-auto flex w-full max-w-[1280px] flex-col items-center gap-4 px-4 py-24 text-center md:px-8">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-emotion-sad-line h-7 w-7 flex items-center justify-center"></i>
        </span>
        <h1 className="font-heading text-2xl font-black text-foreground-950">话题不存在或已下架</h1>
        <p className="text-sm text-foreground-600">
          这条瓜可能已经被整理或替换，回到榜单看看当前正在讨论什么。
        </p>
        <Link
          to="/rank"
          className="mt-2 rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回吃瓜榜单
        </Link>
      </section>
    );
  }

  const category = topicCategories.find((cat) => cat.slug === topic.categorySlug);
  const related = hotTopics
    .filter((item) => item.id !== topic.id && item.categorySlug === topic.categorySlug)
    .slice(0, 3);
  const fallbackRelated = hotTopics
    .filter((item) => item.id !== topic.id && item.categorySlug !== topic.categorySlug)
    .slice(0, 3 - related.length);
  const recommend = [...related, ...fallbackRelated].slice(0, 3);

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: topic.title,
      description: topic.summary,
      datePublished: topic.date,
      dateModified: topic.date,
      articleSection: topic.category,
      author: { "@type": "Organization", name: "黑料瓜网" },
      publisher: { "@type": "Organization", name: "黑料瓜网" },
      mainEntityOfPage: `https://duopingheyi.cn/topic/${topic.id}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://duopingheyi.cn/" },
        { "@type": "ListItem", position: 2, name: "吃瓜榜单", item: "https://duopingheyi.cn/rank" },
        {
          "@type": "ListItem",
          position: 3,
          name: topic.category,
          item: `https://duopingheyi.cn/category/${topic.categorySlug}`,
        },
        {
          "@type": "ListItem",
          position: 4,
          name: topic.title,
          item: `https://duopingheyi.cn/topic/${topic.id}`,
        },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${topic.title} - 黑料瓜网今日瓜详情`}
        description={topic.summary.slice(0, 110)}
        keywords={`${topic.tags.join(",")},${topic.category},今日瓜,黑料瓜网`}
        path={`/topic/${topic.id}`}
        schema={schema}
      />

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "吃瓜榜单", to: "/rank" },
              { label: topic.category, to: `/category/${topic.categorySlug}` },
              { label: topic.title },
            ]}
          />
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <article className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={`/category/${topic.categorySlug}`}
                className="flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 font-label text-xs font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
              >
                <i className={`${category?.icon ?? "ri-price-tag-3-line"} h-4 w-4 flex items-center justify-center`}></i>
                {topic.category}
              </Link>
              <span className="font-label text-xs text-foreground-500">更新于 {topic.date}</span>
            </div>

            <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
              {topic.title}
            </h1>

            <p className="mt-4 border-l-4 border-primary-500 bg-background-100 px-4 py-3 text-sm leading-relaxed text-foreground-700 md:text-base">
              {topic.summary}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-foreground-600">
              <span className="flex items-center gap-1 font-medium text-primary-600">
                <i className="ri-fire-fill h-4 w-4 flex items-center justify-center"></i>
                热度 {topic.heat}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-chat-3-line h-4 w-4 flex items-center justify-center"></i>
                {topic.comments} 条讨论
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-user-line h-4 w-4 flex items-center justify-center"></i>
                {topic.author}
              </span>
            </div>

            <div className="mt-6 h-72 w-full overflow-hidden rounded-lg md:h-96">
              <img
                src={topic.image}
                alt={topic.title}
                title={`${topic.title} 娱乐热点话题`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="mt-8 flex flex-col gap-5">
              {topic.content.map((paragraph, index) => (
                <p key={index} className="text-base leading-loose text-foreground-800">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
              <span className="font-label text-sm font-bold text-foreground-800">相关标签：</span>
              {topic.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/rank?q=${encodeURIComponent(tag)}`}
                  className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3 py-1 font-label text-xs text-foreground-700 transition-colors hover:bg-secondary-100"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <p className="mt-6 rounded-md border border-background-200 bg-background-100 px-4 py-3 text-xs leading-relaxed text-foreground-500">
              内容说明：本页所涉及人物与事件均为虚构化处理，仅用于娱乐话题整理与讨论参考，不针对任何真实个人。
            </p>
          </article>

          <aside className="lg:col-span-1">
            <div className="sticky top-28 flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-links-line h-5 w-5 flex items-center justify-center text-primary-500"></i>
                  相关推荐
                </h2>
                <div className="mt-4 flex flex-col gap-4">
                  {recommend.map((item) => (
                    <Link
                      key={item.id}
                      to={`/topic/${item.id}`}
                      className="group flex gap-3"
                    >
                      <span className="relative block h-16 w-20 shrink-0 overflow-hidden rounded-md">
                        <img
                          src={item.image}
                          alt={item.title}
                          title={`${item.title} 相关话题`}
                          className="h-full w-full object-cover object-top"
                        />
                      </span>
                      <span className="flex flex-1 flex-col">
                        <span className="line-clamp-2 font-heading text-sm font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                          {item.title}
                        </span>
                        <span className="mt-1 font-label text-xs text-foreground-500">
                          {item.category} · 热度 {item.heat}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-5">
                <h2 className="font-heading text-lg font-bold text-secondary-950">
                  也有线索想补充？
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-secondary-900/80">
                  如果你掌握这条话题的补充信息，可以到首页的爆料投稿入口提交，我们会做核实与脱敏。
                </p>
                <Link
                  to="/about#contact"
                  className="mt-4 inline-flex items-center gap-1 rounded-md bg-secondary-600 px-4 py-2 font-label text-sm font-medium text-background-50 transition-colors hover:bg-secondary-700"
                >
                  <i className="ri-megaphone-line h-4 w-4 flex items-center justify-center"></i>
                  去投稿
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="w-full border-t border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
          <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            你可能还想吃这些瓜
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {recommend.map((item) => (
              <TopicCard key={item.id} topic={item} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}